import React, {memo, useState, useEffect} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Grid,
    Paper,
    TextField,
} from '@material-ui/core';
import {Alert } from "@material-ui/lab";
import AddIcon from '@material-ui/icons/Add';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import {useLazyQuery} from '@apollo/react-hooks';
import {useMutation} from '@apollo/react-hooks';
import {gql} from 'apollo-boost';
import useFormInputs from "../../hooks/useFormInputs";
import useStyles from '../../styles/styles';
import axios from 'axios';
import RenderInput from "../TrafficSource/RenderInput";

const CampaignFilterListControl = memo(({data: componentData}) => {
    const classes = useStyles();
    const [variables, setVariables] = useState({});
    const [isLoading, setIsLoading] = useState('');
    const [success, setSuccess] = useState(false);
    const [error, setIsError] = useState(false);

    const onSubmit = () => {
        let error = false;
        setIsError(error);
        if (inputs.file.uploaded && inputs.file.fileSizeChecked && inputs.file.fileSizePassed) {
            Object.keys(inputs).forEach(key => {
                const value = inputs[key];
                if (value === '') {
                    error = true;
                }
            });
            if (!error) {
                setVariables({
                    filterListName: inputs.filterListName,
                    filterListFieldType: inputs.filterListFieldType,
                    bucketName: componentData.getPresignedUrl.bucketName,
                    keyName: componentData.getPresignedUrl.keyName
                });
            }
        } else {
            error = true;
        }
        setIsError(error);
    };
    const {inputs, setInputs, handleInputChange, handleSubmit} = useFormInputs(onSubmit);
    const resetInputs = () => {
        setInputs(inputs => ({
            ...inputs,
            filterListName: '',
            filterListFieldType: '',
            bucketName: componentData.getPresignedUrl.bucketName,
            keyName: componentData.getPresignedUrl.keyName,
            file: {
                uploaded: false,
                fileSizeChecked: false,
                fileSizePassed: false,
                name: ''
            }
        }));
    }
    useEffect(() => {
        resetInputs();
    }, [componentData]);

    const [createFilterList, {loading: createFilterListLoading, error: createFilterListError, data: createFilterListData}] = useMutation(gql`
        mutation CampaignFilterListMutation(
            $filterListName: String!
            $filterListFieldType: FilterFields!
            $bucketName: String!
            $keyName: String!
        ) {
            createOrUpdateList(
                listName: $filterListName
                fieldType: $filterListFieldType,
                bucketName: $bucketName,
                keyName: $keyName
            ) {
                id
                name
                field
            }
        }
    `, {
        variables: {
            ...variables,
        }
    });

    useEffect(() => {
        if (createFilterListData) {
            resetInputs();
            setIsLoading('');
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        }
    }, [createFilterListData]);

    useEffect(() => {
        if (createFilterListLoading) {
            setIsLoading('mutation');
        }
    }, [createFilterListLoading]);


    useEffect(() => {
        if (Object.keys(variables).length) {
            createFilterList();
        }
    }, [variables]);

    const [s3Object, {loading: s3ObjectLoading, error: s3ObjectError, data: s3ObjectData}] = useLazyQuery(gql`
        query S3Object($bucketName: String!, $keyName: String!) {
            getObjectLineCount(bucketName: $bucketName, keyName: $keyName)
        }
    `, {
        fetchPolicy: "network-only"
    });

    useEffect( () => {
        if (s3ObjectData) {
            setIsLoading('');

            let fileSizePassed = false;
            if (s3ObjectData.getObjectLineCount <= componentData.campaignFilterListMutationMaxRecords) {
                fileSizePassed = true;
            }

            setInputs(inputs => ({
                ...inputs,
                file: {
                    ...inputs.file,
                    fileSizeChecked: true,
                    fileSizePassed,
                }
            }));
        }
    }, [s3ObjectData]);

    useEffect(() => {
        if (s3ObjectLoading) {
            setIsLoading('s3object')
        }
    }, [s3ObjectLoading]);

    useEffect(() => {
        if (inputs && inputs.file && inputs.file.uploaded && !inputs.file.fileSizeChecked) {
            s3Object({
                variables: {
                    bucketName: componentData.getPresignedUrl.bucketName,
                    keyName: componentData.getPresignedUrl.keyName
                }
            });
        }
    }, [inputs])

    /**
     * File Upload Change Handler
     * @param event
     */
    const onFileUpload = event => {
        setIsLoading('upload');
        axios.put(componentData.getPresignedUrl.url, event.target.files[0], {
            headers: {
                'Content-Type': 'text/plain',
            }
        })
            .then(response => {
                const { config } = response;
                setInputs(inputs => ({
                    ...inputs,
                    file: {
                        ...inputs.file,
                        uploaded: true,
                        name: config.data.name,
                        fileSizeChecked: false,
                        fileSizePassed: false,
                    }
                }));
                setIsLoading('');
            })
            .catch(error => {
                setInputs(inputs => ({
                    ...inputs,
                    file: {
                        ...inputs.file,
                        uploaded: false,
                        name: '',
                        fileSizeChecked: false,
                        fileSizePassed: false,
                    }
                }));
                setIsLoading('');
            });
    };

    return (
        <form id="campaign-filter-list-update-form">
            <Container maxWidth={false}>
                {s3ObjectError ? (
                    <Paper style={{ height: '300px', overflowY: 'scroll'}}>
                        <Alert severity="error">There is an error!</Alert>
                        <pre>{JSON.stringify(s3ObjectError, null, 2)}</pre>
                    </Paper>
                ): null}
                {createFilterListError ? (
                    <Paper style={{ height: '300px', overflowY: 'scroll'}}>
                        <Alert severity="error">There is an error!</Alert>
                        <pre>{JSON.stringify(createFilterListError, null, 2)}</pre>
                    </Paper>
                ): null}
                {success ? (
                    <Alert severity="success" style={{ marginBottom: '10px' }}>
                        File Uploaded to Database, please confirm!
                    </Alert>
                ) : null}
                <Box className={classes.filterBox} style={{marginTop: 0}}>
                    {isLoading === 'mutation' ? (
                        <CircularProgress />
                    ) : (
                        <>
                            {error ? (
                                inputs.file.fileSizeChecked && !inputs.file.fileSizePassed ? (
                                        <Alert severity="error" style={{ marginBottom: '10px' }}>
                                            Selected file is too big.  Upload a file that has no more than {componentData.campaignFilterListMutationMaxRecords} lines!
                                        </Alert>

                                    ) : (
                                        <Alert severity="error" style={{ marginBottom: '10px' }}>
                                            Please add values to the highlighed fields!
                                        </Alert>
                                    )
                            ) : null}
                            <Chip
                                label="Create or Update List"
                                className={classes.filterSectionLabels}
                            />
                            <Grid container spacing={2} alignItems={"center"}>
                                <Grid item xs={2}>
                                    <TextField
                                        id="filter-list-name"
                                        name="filterListName"
                                        label="Filter List Name"
                                        value={inputs.filterListName || ''}
                                        onChange={handleInputChange}
                                        fullWidth
                                        error={inputs.filterListName === ''}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <RenderInput
                                        item={{
                                            label: "Type",
                                            key: "type",
                                            type: "autoComplete",
                                            required: true,
                                            val: "allFilterFields",
                                            returnKey: 'name',
                                            displayKey: 'name'
                                        }}
                                        value={inputs.filterListFieldType || ''}
                                        data={componentData}
                                        handleFilterChange={value => {
                                            setInputs(inputs => ({
                                                ...inputs,
                                                filterListFieldType: value
                                            }));
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    {isLoading === 'upload' || isLoading === 's3object' ? (
                                        <CircularProgress />
                                    ) : null}
                                    {isLoading === '' ? (
                                        <Grid container spacing={2} alignItems={"center"}>
                                            <Grid item xs={6}>
                                                <Button
                                                    startIcon={<AttachFileIcon/>}
                                                    variant="contained"
                                                    color="secondary"
                                                    component="label"
                                                >
                                                    Upload File
                                                    <input
                                                        type="file"
                                                        hidden
                                                        name="file"
                                                        onChange={onFileUpload}
                                                    />
                                                </Button>
                                            </Grid>
                                            <Grid item xs={6}>
                                                {inputs.file && inputs.file.uploaded ? (
                                                        (inputs.file.fileSizeChecked ? (
                                                            (inputs.file.fileSizePassed ? (
                                                                <Alert severity="success">
                                                                    {inputs.file.name}
                                                                </Alert>
                                                            ) : (
                                                                <Alert severity="error">
                                                                    File Too Big
                                                                </Alert>
                                                            ))
                                                        ) : (
                                                            <Alert severity="warning">
                                                                Checking File Size...
                                                            </Alert>
                                                        ))
                                                ) : (
                                                    <Alert severity="error">
                                                        Missing File
                                                    </Alert>
                                                )}
                                            </Grid>
                                        </Grid>
                                    ) : null}
                                </Grid>
                                <Grid item xs={3}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSubmit}
                                        startIcon={<AddIcon/>}>
                                        Create / Update Filter List
                                    </Button>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Box>
            </Container>
        </form>
    );
});

export default CampaignFilterListControl;
